import { useRef, useState } from 'react'

import {
  Alert,
  Field,
  IconArrowLineDown,
  IconCloudArrowUp,
  IconFileText,
  IconTrash,
  Label,
  Modal,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  Spinner,
  Text,
  Textarea
} from '@vtex/shoreline'

import { Button } from '~/components/Buttons'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import styles from '../../../components/advertiserBrand/styles.module.css'
import { useAdvertiserBrandImport } from '../../../hooks/useAdvertiserBrandImport'
import { buildReviewRows } from './import/reviewRows'
import { ReviewSummary } from './import/ReviewSummary'
import { ReviewTable } from './import/ReviewTable'

type ImportStep = 'input' | 'review'

const TEMPLATE_PATH = '/modelo-importacao-marcas.csv'
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024

/**
 * Wizard input → processing → review. O `dry_run` do BFF valida linha a
 * linha antes de gravar; a confirmação reenvia o mesmo arquivo sem `dry_run`.
 */
export const ImportCsvModal: React.FC<{
  open: boolean
  onClose: () => void
}> = ({ open, onClose }) => {
  const { t } = useAPPTranslation()
  const { preview, confirm, getErrorMessage } = useAdvertiserBrandImport()

  const [step, setStep] = useState<ImportStep>('input')
  const [file, setFile] = useState<File | null>(null)
  const [pasted, setPasted] = useState('')
  const [previewData, setPreviewData] =
    useState<AdvertiserBrandImportPreviewResponse>()
  const [structuralError, setStructuralError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setStep('input')
    setFile(null)
    setPasted('')
    setPreviewData(undefined)
    setStructuralError(null)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const buildFile = () =>
    file ??
    (pasted.trim()
      ? new File([pasted], 'colado.csv', { type: 'text/csv' })
      : null)

  const handleValidate = async () => {
    const payload = buildFile()

    if (!payload) return

    if (payload.size > MAX_FILE_SIZE_BYTES) {
      setStructuralError(
        t('rm:advertiserProducts.import.fileTooBig', {
          max: MAX_FILE_SIZE_BYTES / (1024 * 1024)
        })
      )

      return
    }

    setStructuralError(null)

    try {
      setPreviewData(await preview.mutateAsync(payload))
      setStep('review')
    } catch (error) {
      setStructuralError(getErrorMessage(error))
    }
  }

  const handleConfirm = async () => {
    const payload = buildFile()

    if (!payload) return

    try {
      await confirm.mutateAsync(payload)
      handleClose()
    } catch {
      // o toast da mutation já avisou; o review fica aberto para tentar de novo
    }
  }

  const productNotFoundLabel = t(
    'rm:advertiserProducts.import.review.productNotFound'
  )
  const reviewRows = previewData
    ? buildReviewRows(previewData, productNotFoundLabel)
    : []

  const dropzoneClass = `${styles.dropzone} ${
    isDragging ? styles.dropzoneActive : ''
  } my-3`

  /** Soltar arquivo vale tanto na área vazia quanto com um arquivo já anexado. */
  const dropProps = {
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault()
      setIsDragging(true)
    },
    onDragLeave: () => setIsDragging(false),
    onDrop: (event: React.DragEvent) => {
      event.preventDefault()
      setIsDragging(false)
      setFile(event.dataTransfer.files?.[0] ?? null)
      setPasted('')
    }
  }

  return (
    <Modal open={open} onClose={handleClose} size="large">
      <ModalHeader>
        <ModalHeading>{t('rm:advertiserProducts.import.title')}</ModalHeading>
        <ModalDismiss />
      </ModalHeader>

      <ModalContent>
        {step === 'input' && preview.isPending && (
          <div className="d-flex align-items-center gap-2 py-4">
            <Spinner
              description={t('rm:advertiserProducts.import.processing')}
            />
            <Text variant="body">
              {t('rm:advertiserProducts.import.processing')}
            </Text>
          </div>
        )}

        {step === 'input' && !preview.isPending && (
          <>
            <Text variant="body">
              {t('rm:advertiserProducts.import.description')}
            </Text>

            {!!structuralError && (
              <Alert variant="critical">{structuralError}</Alert>
            )}

            {file ? (
              <div className={dropzoneClass} {...dropProps}>
                <IconFileText width={24} height={24} />
                <span className={styles.dropzoneFile}>{file.name}</span>
                <span className={styles.dropzoneActions}>
                  <Button
                    template="transparent"
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {t('rm:advertiserProducts.import.file.replace')}
                  </Button>
                  <Button
                    template="transparent"
                    size="small"
                    iconLeft={<IconTrash width={16} height={16} />}
                    text={t('rm:advertiserProducts.import.file.remove')}
                    onClick={() => setFile(null)}
                  />
                </span>
              </div>
            ) : (
              <button
                type="button"
                className={`${dropzoneClass} ${styles.dropzoneButton}`}
                {...dropProps}
                onClick={() => fileInputRef.current?.click()}
              >
                <IconCloudArrowUp width={24} height={24} />
                <Text variant="body">
                  {t('rm:advertiserProducts.import.dropzone')}
                </Text>
              </button>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.tsv,text/csv,text/tab-separated-values,text/plain"
              hidden
              onChange={event => {
                setFile(event.target.files?.[0] ?? null)
                setPasted('')
              }}
              aria-label={t('rm:advertiserProducts.import.dropzone')}
            />

            <Field className={styles.pasteField}>
              <Label>{t('rm:advertiserProducts.import.paste.label')}</Label>
              <Textarea
                value={pasted}
                onChange={value => {
                  setPasted(value)

                  if (value.trim()) setFile(null)
                }}
                placeholder={t(
                  'rm:advertiserProducts.import.paste.placeholder'
                )}
                style={{ minHeight: 140 }}
              />
            </Field>
          </>
        )}

        {step === 'review' && !!previewData && (
          <>
            <ReviewSummary rows={reviewRows} valid={previewData.valid} />
            <ReviewTable rows={reviewRows} />
          </>
        )}
      </ModalContent>

      <ModalFooter>
        {step === 'review' ? (
          <>
            <Button
              template="transparent"
              size="medium"
              onClick={() => setStep('input')}
            >
              {t('rm:advertiserProducts.import.review.back')}
            </Button>
            <Button template="transparent" size="medium" onClick={handleClose}>
              {t('rm:button.Cancel')}
            </Button>
            <Button
              template="primary"
              size="medium"
              disabled={!previewData?.valid}
              loading={confirm.isPending}
              onClick={handleConfirm}
            >
              {t('rm:advertiserProducts.import.submit', {
                count: previewData?.valid ?? 0
              })}
            </Button>
          </>
        ) : (
          <>
            <a
              className={styles.footerLink}
              href={TEMPLATE_PATH}
              download="modelo-importacao-marcas.csv"
            >
              <IconArrowLineDown width={16} height={16} />
              {t('rm:advertiserProducts.import.template')}
            </a>
            <Button template="transparent" size="medium" onClick={handleClose}>
              {t('rm:button.Cancel')}
            </Button>
            <Button
              template="primary"
              size="medium"
              disabled={!file && !pasted.trim()}
              loading={preview.isPending}
              onClick={handleValidate}
            >
              {t('rm:advertiserProducts.import.send')}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  )
}
